import type { ReactNode } from 'react';

export interface StepItem {
  key: string;
  label: ReactNode;
  /** Dòng phụ (thời điểm, người thực hiện…). */
  hint?: ReactNode | undefined;
}

export type StepState = 'done' | 'current' | 'todo' | 'bad';

/** Trạng thái từng bước (thuần — test được): trước `current` = done, `current` = current (hoặc bad khi `failed`), sau = todo; `current` không có trong danh sách → mọi bước todo. */
export function stepStates(
  keys: string[],
  current: string | null | undefined,
  opts: { failed?: boolean | undefined; completed?: boolean | undefined } = {},
): StepState[] {
  if (opts.completed) return keys.map(() => 'done');
  const idx = current ? keys.indexOf(current) : -1;
  return keys.map((_, i) =>
    idx < 0 ? 'todo' : i < idx ? 'done' : i === idx ? (opts.failed ? 'bad' : 'current') : 'todo',
  );
}

/**
 * T6-A (S-9) — thanh bước trạng thái theo mockup v152 (LOGI_STEPS / OEM_STEPS / WO_STEPS): các bước của máy trạng thái, bước hiện tại nổi bật.
 * `failed` = dừng ở bước hiện tại vì hủy/lỗi (tô đỏ). `completed` = xong hết. Chỉ hiển thị — chuyển trạng thái vẫn qua ActionBar.
 */
export function Stepper({
  steps,
  current,
  failed,
  completed,
  ariaLabel = 'Tiến trình',
}: {
  steps: StepItem[];
  current: string | null | undefined;
  failed?: boolean | undefined;
  completed?: boolean | undefined;
  ariaLabel?: string | undefined;
}) {
  const states = stepStates(
    steps.map((s) => s.key),
    current,
    { failed, completed },
  );
  return (
    <ol
      className="kx-steps kx-stepper"
      aria-label={ariaLabel}
      style={{ gridTemplateColumns: `repeat(${Math.max(1, steps.length)}, minmax(0, 1fr))` }}
    >
      {steps.map((s, i) => {
        const st = states[i] ?? 'todo';
        return (
          <li key={s.key} className={st} aria-current={st === 'current' || st === 'bad' ? 'step' : undefined}>
            <span className="kx-step-no" aria-hidden="true">
              {st === 'done' ? '✓' : st === 'bad' ? '✕' : i + 1}
            </span>
            <span className="kx-step-body">
              <b>{s.label}</b>
              {s.hint ? <small>{s.hint}</small> : null}
            </span>
          </li>
        );
      })}
    </ol>
  );
}
